import type { InvestingStyleId } from '../types';
import { INVESTING_STYLE_IDS } from '../types';

/** Screener clause strings keyed by onboarding investing style id (step 2). */
export const INVESTING_STYLE_SCREENER_QUERIES: Record<InvestingStyleId, string[]> = {
  growth: [
    'revenue_growth_past_5_years_cagr > 15%',
    'has_eps_improved_past_5_years = true',
    'gross_profit_margin_ttm > 30%',
    'operating_profit_margin_ttm > 10%',
    'debt_to_equity_ratio_current < 1',
  ],
  steady_compounder: [
    'revenue_growth_past_5_years_cagr > 10%',
    'eps_growth_past_5_years_cagr > 10%',
    'return_on_equity_ttm > 15%',
    'gross_profit_margin_ttm > 40%',
    'operating_profit_margin_ttm > 15%',
    'free_cash_flow_ttm > 10M',
  ],
  value: [
    'price_to_earnings_ratio < 20',
    'return_on_equity_ttm > 15%',
    'debt_to_equity_ratio_current < 1',
    'is_free_cash_flow_positive_past_3_years = true',
    'revenue_growth_past_5_years_cagr > 5%',
  ],
  dividend: [
    'dividend_yield > 2%',
    'free_cash_flow_ttm > 0',
    'debt_to_equity_ratio_current < 1',
    'has_eps_improved_past_3_years = true',
  ],
  founder_led: ['founder_ceo_operator = true'],
};

export function getInvestingStyleScreenerQueries(styleId: InvestingStyleId): string[] {
  return INVESTING_STYLE_SCREENER_QUERIES[styleId] ?? [];
}

export function hasInvestingStyleScreenerQueries(styleId: InvestingStyleId): boolean {
  return getInvestingStyleScreenerQueries(styleId).length > 0;
}

/** Build preferences map for currently selected styles only. */
export function buildInvestingStyleQueriesPreference(
  styleIds: InvestingStyleId[],
): Partial<Record<InvestingStyleId, string[]>> {
  const result: Partial<Record<InvestingStyleId, string[]>> = {};
  for (const styleId of styleIds) {
    const queries = getInvestingStyleScreenerQueries(styleId);
    if (queries.length > 0) {
      result[styleId] = [...queries];
    }
  }
  return result;
}

/** Ensures every investing style has at least one screener clause. */
export function assertAllInvestingStylesMapped(): void {
  for (const styleId of INVESTING_STYLE_IDS) {
    if (!hasInvestingStyleScreenerQueries(styleId)) {
      throw new Error(`Missing screener queries for onboarding investing style: ${styleId}`);
    }
  }
}

assertAllInvestingStylesMapped();
